import { useUnit } from 'effector-react';
import { makeStyles } from '@mui/styles';
import { $userRole, $account } from '@features/common';
import Logo from '@img/ujin.svg';
import {
  $isCollapsed,
  $screenY,
  $isFixed,
  $menuWithNotificationBadge,
  $showInNav,
  switchMenuCollapse,
} from './models';
import { NavList } from './organisms';

const useStyles = makeStyles({
  navbar: (props) => ({
    position: 'absolute',
    backgroundColor: 'white',
    height: '100vh',
    width: props.isFixed ? 300 : props.isCollapsed ? 75 : 300,
    paddingBottom: 24,
    boxShadow: '0px 4px 15px #e7e7ec',
    zIndex: 1100,
    transition: 'width 0.1s',
  }),
  navbar__content: (props) => ({
    height: '100%',
    display: 'grid',
    gridTemplateRows: 'auto auto 1fr auto',
    gap: 15,
    padding: props.isCollapsed ? '16px 0 0 0' : '16px 0 0 0',
  }),
  navbar__logo: {
    display: 'grid',
    margin: '0 auto',
    transform: 'scale(0.7)',
  },
  navbar__account_logo: {
    margin: '0 auto',
    objectFit: 'cover',
    width: '60px',
    maxHeight: '60px',
  },
  navbar__footer: {
    display: 'grid',
    gap: 10,
    alignContent: 'end',
    justifyContent: 'center',
  },
  navbar__version: {
    textAlign: 'center',
    opacity: 0.5,
    fontSize: 12,
  },
});

export const Navbar = () => {
  const [menu, account, isFixed, screenY, userRole, showInNav, isCollapsed] = useUnit([
    $menuWithNotificationBadge,
    $account,
    $isFixed,
    $screenY,
    $userRole,
    $showInNav,
    $isCollapsed,
  ]);

  const isAccountLogo = account.logo !== 'null' && account.logo;
  const styleProps = {
    isFixed,
    isCollapsed,
  };
  const classes = useStyles(styleProps);

  const userAvailableSections = userRole.sections || {};

  const sectionNav = Object.entries(userAvailableSections)
    .filter(([, value]) => value)
    .map(([key]) => key)
    .filter((section) => showInNav.includes(section));

  const availableSections = menu.items
    .reduce((acc, item) => {
      if (item.nest) {
        const subitems = item.items.filter((subitem) =>
          sectionNav.includes(`${subitem.link}`)
        );

        if (subitems.length === 1) {
          return acc.concat({
            icon: item.icon,
            activeIcon: item.activeIcon,
            ...subitems[0],
          });
        }

        return acc.concat({ ...item, items: subitems });
      }
      return sectionNav.includes(`${item.link}`) ? acc.concat(item) : acc;
    }, [])
    .filter((item) => (item.nest && item.items.length > 0) || !item.nest);

  const handleMouseEvents = (event) => {
    if (isFixed) return null;

    switch (event.type) {
      case 'mouseenter':
        switchMenuCollapse(false);
        break;

      case 'mouseleave':
        switchMenuCollapse(true);
        break;

      default:
        return null;
    }

    return null;
  };

  return (
    <aside
      className={classes.navbar}
      onMouseEnter={handleMouseEvents}
      onMouseLeave={handleMouseEvents}
    >
      <div className={classes.navbar__content}>
        {isAccountLogo ? (
          <img src={account.logo} className={classes.navbar__account_logo} />
        ) : (
          <Logo className={classes.navbar__logo} alt="Ujin" />
        )}

        <NavList
          isCollapsed={isCollapsed}
          screenY={screenY}
          items={availableSections !== null ? availableSections : menu.items}
        />

        <div className={classes.navbar__footer}>
          <span className={classes.navbar__version}>{process.env.BUILD_VERSION}</span>
        </div>
      </div>
    </aside>
  );
};
